import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import {
  INTERPOLATION_DELAY_MS,
  MOVE_SPEED_TILES_PER_SEC,
  RUN_SPEED_MULT,
  type PlayerPublicState,
  type Point,
} from "@dogdell/shared";
import { applyLabelLod } from "./LabelLod";
import { makeContactShadow } from "./ContactShadow";
import { DOG_WALK_FRAMES } from "./DogBreeds";
import { stepSoakVisual, WetDropVfx } from "./WetDropVfx";
import {
  billboardMaterial,
  CHAR_RIM_COLOR,
  CHAR_RIM_HOVER,
  faceCameraYaw,
  facingFlipSign,
  makeSilhouette,
  placeRimBehind,
  setSilhouetteColor,
  setSilhouetteMap,
  SPRITE_FOOT_Y,
  SPRITE_FOOT_Y_CROUCH,
  type WorldArt,
} from "./WorldArt";

interface Sample {
  at: number;
  x: number;
  y: number;
  facing: number;
}

/** Soft cute trot cycles per second. */
const TROT_HZ = 2.4;
/** Soft follow for remote players only. */
const REMOTE_FOLLOW = 12;
/**
 * Only hard-snap the local pup this far from authority.
 * Smaller gaps are closed by speed matching - the old 1.6-tile snap
 * felt like random forward teleports after long walks.
 */
const LOCAL_RESYNC_TILES = 4.5;
const ARRIVE = 0.035;
const FACING_LERP = 9;
/** Keep trotting this long after the last real step so gait doesn't die mid-slide. */
const TROT_HOLD_MS = 180;
/** Local catch-up toward server when the client path has drained. */
const CATCHUP_SPEED = MOVE_SPEED_TILES_PER_SEC;
/** Nameplate bottom above sprite top (CSS2D grows upward via translate -100%). */
const NAME_LIFT = 0.18;
/** Chat bubble bottom - clears two-line player nameplate (name + Total Lv). */
const BUBBLE_LIFT = 1.05;
/** Sprinkler soak - sprite tint at full wetness (darker + cooler). */
const WET_SPRITE_R = 0.62;
const WET_SPRITE_G = 0.74;
const WET_SPRITE_B = 0.9;
/** Baked cream rim shifts toward this blue at full wetness. */
const WET_RIM_COLOR = 0x81d4fa;
const WET_RIM_A = new THREE.Color(CHAR_RIM_COLOR);
const WET_RIM_B = new THREE.Color(WET_RIM_COLOR);
const WET_RIM_LERP = new THREE.Color();

export class DogActor {
  readonly group: THREE.Group;
  /** Raycast target for hover / sniff. */
  readonly hitMesh: THREE.Mesh;
  private readonly sprite: THREE.Mesh;
  private readonly outline: THREE.Mesh;
  private readonly nameEl: HTMLDivElement;
  private readonly bubbleEl: HTMLDivElement;
  private readonly progressEl: HTMLDivElement;
  private readonly nameObj: CSS2DObject;
  private readonly bubbleObj: CSS2DObject;
  /** Remote interpolation buffer. */
  private samples: Sample[] = [];
  /** Local client-side path (tile coords); drained smoothly each frame. */
  private localPath: Point[] = [];
  private fur: number;
  private collar: string | null;
  private displayX = 0;
  private displayY = 0;
  private visualFacing = 0;
  private gaitPhase = 0;
  private moving = false;
  private frame = 0;
  private lastUpdate = performance.now();
  private prevDisplayX = 0;
  private prevDisplayY = 0;
  /** Wall-clock ms - keep legs cycling until this time. */
  private trotHoldUntil = 0;
  private highlighted = false;
  /** 0 = dry, 1 = soaked (sprinkler / water). */
  private wetness = 0;
  private dryElapsedSec = 0;
  private wasSoaked = false;
  private readonly wetDrops: WetDropVfx;
  /** Local jog (Shift / Run toggle); mirrors server `running`. */
  private running = false;
  private readonly floats: { object: CSS2DObject; started: number; life: number }[] = [];

  get poseX(): number {
    return this.displayX;
  }

  get poseY(): number {
    return this.displayY;
  }

  /** World facing (radians) - used for billboards and heading-up minimap. */
  get facing(): number {
    return this.visualFacing;
  }

  get playerId(): string {
    return this.state.id;
  }

  /** True while the local pup still has click-to-move waypoints queued. */
  get hasPath(): boolean {
    return this.localPath.length > 0;
  }

  setRunning(on: boolean): void {
    if (!this.local) {
      return;
    }
    this.running = on;
  }

  /** Stay soaked while in water; 2s hold + 5s fade after leaving (7s total). */
  tickWetness(soaking: boolean, dtSec: number): void {
    const next = stepSoakVisual(soaking, this.dryElapsedSec, this.wasSoaked, dtSec);
    this.wetness = next.visual;
    this.dryElapsedSec = next.dryElapsedSec;
    this.wasSoaked = next.wasSoaked;
    this.wetDrops.setWetness(this.wetness);
    this.applyWetness();
  }

  constructor(
    private readonly art: WorldArt,
    public state: PlayerPublicState,
    private readonly local: boolean,
    private readonly groundY: (poseX: number, poseY: number) => number = () => 0,
  ) {
    this.fur = state.color;
    this.collar = null;
    this.displayX = state.x;
    this.displayY = state.y;
    this.prevDisplayX = state.x;
    this.prevDisplayY = state.y;
    this.visualFacing = state.facing;
    this.samples.push({ at: performance.now(), x: state.x, y: state.y, facing: state.facing });

    this.group = new THREE.Group();
    this.group.position.set(state.x + 0.5, this.groundY(state.x, state.y), state.y + 0.5);

    const size = art.dogSize(state.color);
    const shadow = makeContactShadow({
      radius: Math.min(0.62, size.width * 0.42),
      squash: 1,
      opacity: 0.9,
    });
    this.group.add(shadow);

    const map = art.dog(state.color, collarColor(state));
    this.outline = makeSilhouette(size.width, size.height, map);
    this.outline.position.y = size.height * SPRITE_FOOT_Y;
    this.group.add(this.outline);

    this.sprite = new THREE.Mesh(
      new THREE.PlaneGeometry(size.width, size.height),
      billboardMaterial(map),
    );
    this.sprite.position.y = size.height * SPRITE_FOOT_Y;
    this.sprite.renderOrder = 2;
    this.sprite.userData.dogActor = true;
    this.group.add(this.sprite);
    this.hitMesh = this.sprite;
    this.wetDrops = new WetDropVfx(this.sprite);

    this.nameEl = document.createElement("div");
    this.nameEl.className = "world-label world-name world-player";
    this.nameEl.classList.toggle("is-local", local);
    this.nameEl.innerHTML = playerNameplateHtml(state.name, state.totalLevel ?? 3);
    this.nameObj = new CSS2DObject(this.nameEl);
    this.nameObj.position.set(0, size.height + NAME_LIFT, 0);
    this.group.add(this.nameObj);

    this.bubbleEl = document.createElement("div");
    this.bubbleEl.className = "world-label world-bubble";
    this.bubbleEl.hidden = true;
    this.bubbleObj = new CSS2DObject(this.bubbleEl);
    // Sit clearly above the two-line nameplate (name + Total Lv).
    this.bubbleObj.position.set(0, size.height + BUBBLE_LIFT, 0);
    this.group.add(this.bubbleObj);

    this.progressEl = document.createElement("div");
    this.progressEl.className = "world-progress";
    this.progressEl.hidden = true;
    const progressObj = new CSS2DObject(this.progressEl);
    progressObj.position.set(0, size.height + 0.02, 0);
    this.group.add(progressObj);
  }

  snapPose(x: number, y: number): void {
    this.displayX = x;
    this.displayY = y;
    this.prevDisplayX = x;
    this.prevDisplayY = y;
    this.state = { ...this.state, x, y };
    this.localPath.length = 0;
    this.samples = [{ at: performance.now(), x, y, facing: this.visualFacing }];
  }

  applyState(state: PlayerPublicState, now: number): void {
    this.state = state;

    if (this.local) {
      const err = Math.hypot(state.x - this.displayX, state.y - this.displayY);
      // Local visuals own the path. Only hard-snap if badly desynced.
      if (err > LOCAL_RESYNC_TILES) {
        this.displayX = state.x;
        this.displayY = state.y;
        this.prevDisplayX = state.x;
        this.prevDisplayY = state.y;
        this.visualFacing = state.facing;
        this.localPath.length = 0;
      }
      // Catch-up toward authority is continuous in advanceLocal - not a one-shot
      // lerp here (that caused idle-frame sliding between network ticks).
    } else {
      this.samples.push({ at: now, x: state.x, y: state.y, facing: state.facing });
      if (this.samples.length > 16) {
        this.samples.splice(0, this.samples.length - 16);
      }
    }

    if (state.color !== this.fur) {
      this.fur = state.color;
      const size = this.art.dogSize(state.color);
      this.sprite.geometry.dispose();
      this.sprite.geometry = new THREE.PlaneGeometry(size.width, size.height);
      this.sprite.position.y = size.height * SPRITE_FOOT_Y;
      this.outline.geometry.dispose();
      this.outline.geometry = new THREE.PlaneGeometry(size.width, size.height);
      this.outline.position.y = size.height * SPRITE_FOOT_Y;
      this.nameObj.position.y = size.height + NAME_LIFT;
      this.bubbleObj.position.y = size.height + BUBBLE_LIFT;
      this.applyFrame(this.frame);
    }

    this.nameEl.innerHTML = playerNameplateHtml(state.name, state.totalLevel);
    if (state.chat) {
      this.bubbleEl.textContent = state.chat.message;
      this.bubbleEl.hidden = false;
    } else {
      this.bubbleEl.hidden = true;
    }
  }

  /**
   * Local-only: replace the client walk queue with a smooth polyline.
   * Movement is integrated per-frame - not driven by server snapshot timestamps.
   */
  predictPath(waypoints: Point[], _speed = MOVE_SPEED_TILES_PER_SEC): void {
    if (!this.local) {
      return;
    }
    this.localPath = waypoints.map((p) => ({ x: p.x, y: p.y }));
  }

  update(time: number, camera: THREE.Camera, focusX = 0, focusZ = 0): void {
    const dtSec = Math.min(0.05, Math.max(0.001, (time - this.lastUpdate) / 1000));
    this.lastUpdate = time;

    if (this.local) {
      this.advanceLocal(dtSec);
    } else {
      this.advanceRemote(time, dtSec);
    }

    const movedDist = Math.hypot(this.displayX - this.prevDisplayX, this.displayY - this.prevDisplayY);
    const speed = movedDist / dtSec;
    this.prevDisplayX = this.displayX;
    this.prevDisplayY = this.displayY;

    const pathPending = this.local && this.localPath.length > 0;
    const serverGap =
      this.local
        ? Math.hypot(this.state.x - this.displayX, this.state.y - this.displayY)
        : 0;
    // Any real locomotion refreshes the hold so catch-up slides keep trotting.
    if (pathPending || speed >= 0.35 || serverGap > 0.06) {
      this.trotHoldUntil = time + TROT_HOLD_MS;
    }
    this.moving = time < this.trotHoldUntil;

    const begging = this.state.action?.skillId === "begging";
    const trotting = this.moving && !begging;

    if (trotting) {
      const gaitRate = this.running ? TROT_HZ * 1.75 : TROT_HZ;
      this.gaitPhase += dtSec * gaitRate * Math.PI * 2;
      // 8 walk frames (sheet indices 0-7), stored as actor frames 1-8.
      const nextFrame = 1 + (Math.floor((this.gaitPhase / (Math.PI * 2)) * DOG_WALK_FRAMES) % DOG_WALK_FRAMES);
      if (nextFrame !== this.frame) {
        this.frame = nextFrame;
        this.applyFrame(this.frame);
      }
    } else if (this.frame !== 0) {
      this.frame = 0;
      this.applyFrame(0);
    } else {
      this.gaitPhase += dtSec * 1.15;
    }

    const size = this.art.dogSize(this.state.color);
    // Soft landings: sin^2 bounce, gentle breathe when idle.
    const phase = this.gaitPhase;
    const trotBounce = trotting
      ? Math.pow(Math.sin(phase), 2) * 0.028
      : Math.sin(time * 0.0024) * 0.006;
    const breathe = trotting ? 0 : Math.sin(phase) * 0.01;
    const squashY = trotting
      ? 1 + Math.sin(phase * 2) * 0.028 + breathe
      : 1 + Math.sin(phase) * 0.014;
    const squashX = trotting
      ? 1 - Math.sin(phase * 2) * 0.022
      : 1 - Math.sin(phase) * 0.008;
    const tip = trotting ? Math.sin(phase) * 0.012 : Math.sin(time * 0.002) * 0.006;

    this.group.position.set(
      this.displayX + 0.5,
      this.groundY(this.displayX, this.displayY),
      this.displayY + 0.5,
    );
    const crouch = begging ? 0.82 : 1;
    const foot = begging ? SPRITE_FOOT_Y_CROUCH : SPRITE_FOOT_Y;
    // Keep opaque paws on the turf when squashY scales the plane.
    const feetY = size.height * (foot - 0.5) + size.height * 0.5 * squashY + trotBounce;
    this.sprite.position.set(0, feetY, 0);

    faceCameraYaw(this.sprite, camera);

    const flip = facingFlipSign(this.displayX + 0.5, this.displayY + 0.5, this.visualFacing, camera);
    this.sprite.scale.set(flip * crouch * squashX, crouch * squashY, 1);
    // Tip in local space after screen-align (don't overwrite quaternion via rotation.z).
    if (Math.abs(tip) > 1e-5) {
      this.sprite.rotateZ(tip * flip);
    }
    placeRimBehind(this.outline, this.sprite, flip * crouch * squashX, crouch * squashY);

    this.wetDrops.tick(dtSec, size.width * Math.abs(squashX), size.height * crouch * squashY);

    const dist = Math.hypot(this.displayX + 0.5 - focusX, this.displayY + 0.5 - focusZ);
    applyLabelLod(this.nameEl, dist, { always: this.local });

    if (this.state.action) {
      const t = clamp((Date.now() - this.state.action.startedAt) / this.state.action.durationMs, 0, 1);
      this.progressEl.hidden = false;
      this.progressEl.style.background = `conic-gradient(#fff176 ${t * 360}deg, rgba(0,0,0,0.35) 0)`;
    } else {
      this.progressEl.hidden = true;
    }

    const remaining: typeof this.floats = [];
    for (const float of this.floats) {
      const t = (time - float.started) / float.life;
      if (t >= 1) {
        float.object.removeFromParent();
        float.object.element.remove();
        continue;
      }
      float.object.position.y = 1.2 + t * 0.7;
      (float.object.element as HTMLElement).style.opacity = String(1 - t * t);
      remaining.push(float);
    }
    this.floats.length = 0;
    this.floats.push(...remaining);
  }

  /** Frame-integrated local walk - same idea as the server, but continuous. */
  private advanceLocal(dtSec: number): void {
    const gapX = this.state.x - this.displayX;
    const gapY = this.state.y - this.displayY;
    const gap = Math.hypot(gapX, gapY);

    // Match server speed. Speed up only when authority is ahead of us along the
    // path; ease off when we've sprinted ahead (avoids rubber-band snaps).
    let speedScale = 1;
    if (this.localPath.length > 0) {
      const next = this.localPath[0]!;
      const pathDx = next.x - this.displayX;
      const pathDy = next.y - this.displayY;
      const pathLen = Math.hypot(pathDx, pathDy) || 1;
      const along = (gapX * pathDx + gapY * pathDy) / pathLen;
      if (along > 0.35) {
        speedScale = 1.1;
      } else if (along > 0.15) {
        speedScale = 1.04;
      } else if (along < -0.15) {
        speedScale = 0.78;
      } else if (gap < 0.06) {
        speedScale = 0.96;
      }
    }

    let budget = MOVE_SPEED_TILES_PER_SEC * (this.running ? RUN_SPEED_MULT : 1) * speedScale * dtSec;
    while (budget > 0 && this.localPath.length > 0) {
      const next = this.localPath[0]!;
      const dx = next.x - this.displayX;
      const dy = next.y - this.displayY;
      const dist = Math.hypot(dx, dy);
      if (dist <= ARRIVE) {
        this.displayX = next.x;
        this.displayY = next.y;
        this.localPath.shift();
        continue;
      }
      const targetFacing = Math.atan2(dy, dx);
      this.visualFacing = lerpAngle(
        this.visualFacing,
        targetFacing,
        1 - Math.exp(-FACING_LERP * Math.min(0.05, dtSec)),
      );
      if (dist <= budget) {
        this.displayX = next.x;
        this.displayY = next.y;
        this.localPath.shift();
        budget -= dist;
      } else {
        const t = budget / dist;
        this.displayX += dx * t;
        this.displayY += dy * t;
        budget = 0;
      }
    }

    // Client path often finishes a beat before server authority - keep walking
    // toward the server pose so legs don't freeze on an idle frame mid-slide.
    if (this.localPath.length === 0) {
      const dx = this.state.x - this.displayX;
      const dy = this.state.y - this.displayY;
      const dist = Math.hypot(dx, dy);
      if (dist > 0.04) {
        const catchup = CATCHUP_SPEED * (this.running ? RUN_SPEED_MULT : 1);
        const step = Math.min(dist, catchup * dtSec);
        this.displayX += (dx / dist) * step;
        this.displayY += (dy / dist) * step;
        this.visualFacing = lerpAngle(
          this.visualFacing,
          Math.atan2(dy, dx),
          1 - Math.exp(-FACING_LERP * Math.min(0.05, dtSec)),
        );
      }
    }
  }

  private advanceRemote(time: number, dtSec: number): void {
    const renderAt = time - INTERPOLATION_DELAY_MS;
    const target = this.sampleAt(renderAt);
    const follow = 1 - Math.exp(-REMOTE_FOLLOW * dtSec);
    this.displayX += (target.x - this.displayX) * follow;
    this.displayY += (target.y - this.displayY) * follow;
    this.visualFacing = lerpAngle(this.visualFacing, target.facing, follow);
  }

  private applyFrame(frame: number): void {
    const map = this.art.dog(this.state.color, collarColor(this.state), frame);
    const material = this.sprite.material as THREE.MeshBasicMaterial;
    material.map = map;
    material.needsUpdate = true;
    setSilhouetteMap(this.outline, map);
  }

  setHighlighted(on: boolean): void {
    if (this.local || this.highlighted === on) {
      return;
    }
    this.highlighted = on;
    this.applyWetness();
  }

  private applyWetness(): void {
    const w = this.wetness;
    const material = this.sprite.material as THREE.MeshBasicMaterial;
    if (w <= 0.001) {
      material.color.setRGB(1, 1, 1);
    } else {
      material.color.setRGB(
        1 + (WET_SPRITE_R - 1) * w,
        1 + (WET_SPRITE_G - 1) * w,
        1 + (WET_SPRITE_B - 1) * w,
      );
    }

    if (this.highlighted) {
      this.outline.visible = true;
      setSilhouetteColor(this.outline, CHAR_RIM_HOVER);
      return;
    }

    if (w <= 0.02) {
      this.outline.visible = false;
      setSilhouetteColor(this.outline, CHAR_RIM_COLOR);
      return;
    }

    // Blue exterior ring while wet (baked cream rim also cools via sprite tint).
    this.outline.visible = true;
    WET_RIM_LERP.copy(WET_RIM_A).lerp(WET_RIM_B, w);
    setSilhouetteColor(this.outline, WET_RIM_LERP.getHex());
  }

  floatText(text: string, color: string): void {
    const el = document.createElement("div");
    el.className = "world-label world-float";
    el.textContent = text;
    el.style.color = color;
    const object = new CSS2DObject(el);
    object.position.set(0, 1.2, 0);
    this.group.add(object);
    this.floats.push({ object, started: performance.now(), life: 1100 });
  }

  /** Floating reaction glyph (heart, etc.) - visible above this pup for everyone. */
  showReaction(reactionId: string): void {
    const el = document.createElement("div");
    el.className = `world-label world-float world-react world-react-${reactionId}`;
    if (reactionId === "heart") {
      el.innerHTML = `<svg class="ico" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" fill="currentColor" stroke="none"><path d="M12 20.4S3.6 14.8 3.6 9.4A4.4 4.4 0 0 1 12 6.2a4.4 4.4 0 0 1 8.4 3.2C20.4 14.8 12 20.4 12 20.4z"/></svg>`;
    } else {
      el.textContent = "✨";
    }
    const object = new CSS2DObject(el);
    object.position.set(0, 1.35, 0);
    this.group.add(object);
    this.floats.push({ object, started: performance.now(), life: 1500 });
  }

  destroy(): void {
    this.wetDrops.dispose();
    for (const float of this.floats) {
      float.object.removeFromParent();
      float.object.element.remove();
    }
    this.floats.length = 0;
    this.group.traverse((child) => {
      if (child instanceof CSS2DObject) {
        child.element.remove();
      }
      if (child instanceof THREE.Mesh) {
        if (!child.geometry.userData.shared) {
          child.geometry.dispose();
        }
        if (
          child.material instanceof THREE.Material &&
          child !== this.sprite &&
          !child.material.userData.shared
        ) {
          child.material.dispose();
        }
      }
    });
    this.group.removeFromParent();
  }

  private sampleAt(at: number): Sample {
    if (this.samples.length === 0) {
      return { at, x: this.displayX, y: this.displayY, facing: this.visualFacing };
    }
    const first = this.samples[0]!;
    if (at <= first.at) {
      return first;
    }
    const last = this.samples[this.samples.length - 1]!;
    if (at >= last.at) {
      if (this.samples.length >= 2) {
        const prev = this.samples[this.samples.length - 2]!;
        const span = last.at - prev.at || 1;
        const extra = Math.min(0.1, (at - last.at) / span);
        return {
          at,
          x: last.x + (last.x - prev.x) * extra,
          y: last.y + (last.y - prev.y) * extra,
          facing: last.facing,
        };
      }
      return last;
    }
    let previous = first;
    for (const sample of this.samples) {
      if (sample.at >= at) {
        const span = sample.at - previous.at || 1;
        const t = smoothstep(clamp((at - previous.at) / span, 0, 1));
        return {
          at,
          x: previous.x + (sample.x - previous.x) * t,
          y: previous.y + (sample.y - previous.y) * t,
          facing: lerpAngle(previous.facing, sample.facing, t),
        };
      }
      previous = sample;
    }
    return last;
  }
}

function playerNameplateHtml(name: string, totalLevel: number): string {
  return `<span class="world-player-name">${escapeHtml(name)}</span><span class="world-player-lv">Total Lv. ${totalLevel}</span>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function collarColor(_state: PlayerPublicState): number | null {
  return null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerpAngle(from: number, to: number, t: number): number {
  let delta = to - from;
  while (delta > Math.PI) {
    delta -= Math.PI * 2;
  }
  while (delta < -Math.PI) {
    delta += Math.PI * 2;
  }
  return from + delta * t;
}
